import { markSvg, type IconMark } from "@/lib/appIcons";
import { useIconMark } from "@/lib/theme";

/**
 * The list-view icon set — same grid and stroke as NavIcons, so a sidebar
 * glyph and a tab glyph beside each other read as one family.
 *
 * Geometry follows lucide (ISC), which is what the standalone tracker draws
 * with; keeping to it means a glyph here and the same glyph there are the same
 * drawing rather than two people's idea of a folder.
 */

interface IconProps {
  stroke?: number;
  size?: number;
}

/** 1.5px is the tracker's weight (`stroke-[1.5]`), not lucide's default 2. */
function svgProps({ stroke = 1.5, size = 24 }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
}

/** Today — a calendar with a check inside. */
export function CalendarCheckIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M3 10h18" />
      <path d="m9 16 2 2 4-4" />
    </svg>
  );
}

/** Entries — lines of text, the tracker's Entries tab. */
export function TrayIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M21 5H3" />
      <path d="M15 12H3" />
      <path d="M17 19H3" />
    </svg>
  );
}

export function FolderIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}

/** Upcoming — a sun coming up over the horizon. */
export function HorizonIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="M12 2v8" />
      <path d="m4.93 10.93 1.41 1.41" />
      <path d="M2 18h2" />
      <path d="M20 18h2" />
      <path d="m19.07 10.93-1.41 1.41" />
      <path d="M22 22H2" />
      <path d="m8 6 4-4 4 4" />
      <path d="M16 18a4 4 0 0 0-8 0" />
    </svg>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path d="m21 21-4.34-4.34" />
      <circle cx="11" cy="11" r="8" />
    </svg>
  );
}

/**
 * An app icon design on its square (lib/appIcons.ts — the same geometry the
 * generated PNGs are drawn from). `bg` is any CSS colour.
 */
export function AppIcon({ mark, size, radius, bg, fg = "#ffffff" }: {
  mark: IconMark; size: number; radius: number; bg: string; fg?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label="Project Hub"
      style={{ flex: "none", display: "block" }}
    >
      <rect width="100" height="100" rx={(radius / size) * 100} fill={bg} />
      <g dangerouslySetInnerHTML={{ __html: markSvg(mark, fg, bg) }} />
    </svg>
  );
}

/** The brand mark in the app's chrome: your chosen app icon, in the accent. */
export function Logo({ size = 34, radius = 10 }: { size?: number; radius?: number }) {
  const [mark] = useIconMark();
  return <AppIcon mark={mark} size={size} radius={radius} bg="var(--accent)" fg="var(--onAccent)" />;
}
